import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Home } from "lucide-react";
import { Logo, RoleBadge } from "../common/ui";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

/* Shared shell for Admin & Customer portals: fixed sidebar + top bar + content outlet. */
export default function PortalLayout({ navItems, portalName }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="h-16 flex items-center px-5 border-b border-white/10">
        <Logo />
      </div>
      <div className="px-5 py-4 border-b border-white/10">
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{portalName}</p>
        <div className="flex items-center gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        {user?.role && <div className="mt-2"><RoleBadge role={user.role} /></div>}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors border-l-2",
                  isActive
                    ? "bg-gold/10 text-gold border-gold"
                    : "text-white/70 hover:text-white hover:bg-white/5 border-transparent"
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <button
          onClick={() => navigate("/")}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5"
          data-testid="sidebar-back-to-site"
        >
          <Home className="h-4 w-4" /> Back to Site
        </button>
        <button
          onClick={() => logout().then(() => navigate("/"))}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-firered hover:bg-firered/10"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen cf-bg cf-grain">
      <div className="relative z-10 flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 cf-glass border-r border-white/10 z-40">
          <SidebarContent />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <aside className="relative w-72 max-w-[85%] cf-glass border-r border-white/10">
              <SidebarContent />
            </aside>
          </div>
        )}

        <div className="flex-1 lg:pl-72 min-w-0">
          <header className="sticky top-0 z-30 cf-glass border-b border-white/10 h-16 flex items-center justify-between px-4 lg:px-8">
            <button className="lg:hidden text-white p-2" data-testid="portal-menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </button>
            <div className="hidden lg:block">
              <h1 className="font-playfair text-lg text-white">{portalName}</h1>
            </div>
            <div className="flex items-center gap-2 lg:hidden">
              <Logo compact />
            </div>
            <button
              type="button"
              data-testid="topbar-logout"
              onClick={() => logout().then(() => navigate("/"))}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign Out</span>
            </button>
          </header>
          <main className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
